// ============================================================================
// <WorkflowDesigner /> — form-based v1 (no canvas).
// ============================================================================
// Edits a single definition's states / transitions / spawn rules via add/edit
// modals. Actions are listed but managed in a separate add-only flow (v1).
// Polish (drag-drop canvas) is a Phase 8 enhancement.

import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useWorkflowDefinition } from '../../hooks/useWorkflowDefinitions';
import { workflowService } from '../../api/workflowService';
import type {
  CreateSpawnRuleRequest,
  CreateStateRequest,
  CreateTransitionRequest,
  WorkflowChainLinkType,
  WorkflowState,
  WorkflowStateType,
  WorkflowTransition,
} from '../../api/types';

interface Props {
  definitionId: string;
}

const STATE_TYPES: { value: WorkflowStateType; label: string }[] = [
  { value: 'Start', label: 'Start' },
  { value: 'Intermediate', label: 'Intermediate' },
  { value: 'End', label: 'End' },
];

const LINK_TYPES: { value: WorkflowChainLinkType; label: string }[] = [
  { value: 'Spawned', label: 'Spawned' },
  { value: 'Supersedes', label: 'Supersedes' },
  { value: 'Related', label: 'Related' },
];

const sectionStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  padding: '0.75rem 1rem',
  marginBottom: '1rem',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const cellStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderBottom: '1px solid var(--border-color)',
  textAlign: 'left',
};

export const WorkflowDesigner: React.FC<Props> = ({ definitionId }) => {
  const { data, loading, refetch } = useWorkflowDefinition(definitionId);

  const [stateModal, setStateModal] = useState<{ open: boolean; existing?: WorkflowState }>({ open: false });
  const [transitionModal, setTransitionModal] = useState<{ open: boolean; existing?: WorkflowTransition }>({ open: false });
  const [spawnRuleModal, setSpawnRuleModal] = useState<{ open: boolean }>({ open: false });

  const handlePublish = useCallback(async () => {
    if (!data) return;
    try {
      await workflowService.publishDefinition(data.definition.id);
      notificationService.toast.success('Definition published.');
      await refetch();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Publish failed.');
    }
  }, [data, refetch]);

  const handleDeleteState = useCallback(async (id: string) => {
    if (!window.confirm('Delete this state?')) return;
    try { await workflowService.deleteState(id); await refetch(); }
    catch (e: any) { notificationService.toast.error(e?.message ?? 'Delete failed'); }
  }, [refetch]);

  const handleDeleteTransition = useCallback(async (id: string) => {
    if (!window.confirm('Delete this transition?')) return;
    try { await workflowService.deleteTransition(id); await refetch(); }
    catch (e: any) { notificationService.toast.error(e?.message ?? 'Delete failed'); }
  }, [refetch]);

  const handleDeleteSpawnRule = useCallback(async (id: string) => {
    if (!window.confirm('Delete this spawn rule?')) return;
    try { await workflowService.deleteSpawnRule(id); await refetch(); }
    catch (e: any) { notificationService.toast.error(e?.message ?? 'Delete failed'); }
  }, [refetch]);

  if (loading) return <div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Loading definition…</div>;
  if (!data) return <div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Definition not found.</div>;

  const { definition, states, transitions, actions, spawnRules } = data;
  const stateById = new Map(states.map((s) => [s.id, s]));

  return (
    <div style={{ padding: '1rem' }}>
      {/* Definition header */}
      <div style={{ ...sectionStyle, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
              {definition.displayName}
              <span style={{ fontSize: '0.7rem', marginLeft: '0.5rem', color: 'var(--text-secondary)' }}>
                {definition.definitionCode}/v{definition.version}
              </span>
              {definition.isPublished
                ? <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: '#16a34a', fontWeight: 600 }}>● published</span>
                : <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: '#d97706', fontWeight: 600 }}>● draft</span>}
            </h3>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              entity type: <strong>{definition.entityType}</strong>
              {definition.description && <span> · {definition.description}</span>}
            </div>
          </div>
          {!definition.isPublished && (
            <button className="btn btn-primary" onClick={handlePublish}>Publish</button>
          )}
        </div>
      </div>

      {/* States */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <span>States ({states.length})</span>
          <button className="btn btn-secondary" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
            onClick={() => setStateModal({ open: true })}>+ Add state</button>
        </div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={cellStyle}>Code</th>
              <th style={cellStyle}>Display name</th>
              <th style={cellStyle}>Type</th>
              <th style={cellStyle}>TAT (Days)</th>
              <th style={cellStyle}>Escalation Role</th>
              <th style={{ ...cellStyle, width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {states.map((s) => (
              <tr key={s.id}>
                <td style={cellStyle}><code>{s.stateCode}</code></td>
                <td style={cellStyle}>{s.displayName}</td>
                <td style={cellStyle}>{s.stateType}</td>
                <td style={cellStyle}>{s.slaDays ?? '—'}</td>
                <td style={cellStyle}>{s.escalationRoleCode || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={cellStyle}>
                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                    onClick={() => handleDeleteState(s.id)}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Transitions */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <span>Transitions ({transitions.length})</span>
          <button className="btn btn-secondary" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
            onClick={() => setTransitionModal({ open: true })}>+ Add transition</button>
        </div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={cellStyle}>From</th>
              <th style={cellStyle}>Trigger</th>
              <th style={cellStyle}>To</th>
              <th style={cellStyle}>Approval</th>
              <th style={cellStyle}>Permission</th>
              <th style={{ ...cellStyle, width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {transitions.map((t) => (
              <tr key={t.id}>
                <td style={cellStyle}>{stateById.get(t.fromStateId)?.stateCode ?? '?'}</td>
                <td style={cellStyle}><code>{t.triggerCode}</code></td>
                <td style={cellStyle}>{stateById.get(t.toStateId)?.stateCode ?? '?'}</td>
                <td style={cellStyle}>{t.requiresApproval ? `${t.approverRoleCode}` : '—'}</td>
                <td style={cellStyle}>{t.requiredPermission ?? '—'}</td>
                <td style={cellStyle}>
                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                    onClick={() => handleDeleteTransition(t.id)}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions (read-only listing in v1) */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <span>Actions ({actions.length})</span>
        </div>
        {actions.length === 0
          ? <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>No bound actions.</div>
          : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={cellStyle}>Code</th>
                  <th style={cellStyle}>Binding</th>
                  <th style={cellStyle}>Async</th>
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => (
                  <tr key={a.id}>
                    <td style={cellStyle}><code>{a.actionCode}</code></td>
                    <td style={cellStyle}>{a.bindingType}</td>
                    <td style={cellStyle}>{a.isAsync ? 'yes' : 'no'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {/* Spawn rules */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <span>Spawn rules ({spawnRules.length})</span>
          <button className="btn btn-secondary" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
            onClick={() => setSpawnRuleModal({ open: true })}>+ Add spawn rule</button>
        </div>
        {spawnRules.length === 0
          ? <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>No spawn rules.</div>
          : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={cellStyle}>On transition</th>
                  <th style={cellStyle}>Spawns</th>
                  <th style={cellStyle}>Link</th>
                  <th style={cellStyle}>Context map</th>
                  <th style={{ ...cellStyle, width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {spawnRules.map((r) => {
                  const tr = transitions.find((x) => x.id === r.parentTransitionId);
                  return (
                    <tr key={r.id}>
                      <td style={cellStyle}>{tr?.triggerCode ?? '?'}</td>
                      <td style={cellStyle}><code>{r.childDefinitionCode}</code></td>
                      <td style={cellStyle}>{r.linkType}</td>
                      <td style={{ ...cellStyle, fontSize: '0.75rem', fontFamily: 'monospace' }}>{r.contextMap}</td>
                      <td style={cellStyle}>
                        <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                          onClick={() => handleDeleteSpawnRule(r.id)}>Del</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>

      {stateModal.open && (
        <AddStateModal
          definitionId={definitionId}
          onClose={() => setStateModal({ open: false })}
          onSaved={() => { setStateModal({ open: false }); refetch(); }}
        />
      )}
      {transitionModal.open && (
        <AddTransitionModal
          definitionId={definitionId}
          states={states}
          onClose={() => setTransitionModal({ open: false })}
          onSaved={() => { setTransitionModal({ open: false }); refetch(); }}
        />
      )}
      {spawnRuleModal.open && (
        <AddSpawnRuleModal
          definitionId={definitionId}
          transitions={transitions}
          onClose={() => setSpawnRuleModal({ open: false })}
          onSaved={() => { setSpawnRuleModal({ open: false }); refetch(); }}
        />
      )}
    </div>
  );
};

// ----- Embedded modals (kept inline to limit file count) -----

const AddStateModal: React.FC<{
  definitionId: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ definitionId, onClose, onSaved }) => {
  const [form, setForm] = useState<CreateStateRequest>({
    stateCode: '',
    displayName: '',
    stateType: 'Intermediate',
    displayOrder: 0,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.stateCode || !form.displayName) {
      notificationService.toast.warning('State code and display name are required.');
      return;
    }
    setBusy(true);
    try {
      await workflowService.addState(definitionId, form);
      notificationService.toast.success('State added.');
      onSaved();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Add state</h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div className="form-group">
            <label className="form-label">State code (UPPERCASE_SNAKE) *</label>
            <input className="form-input" value={form.stateCode}
              onChange={(e) => setForm({ ...form, stateCode: e.target.value.toUpperCase() })} />
          </div>
          <div className="form-group">
            <label className="form-label">Display name *</label>
            <input className="form-input" value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          </div>
          <div className="form-group">
            <SyutSelect
              label="Type"
              options={STATE_TYPES}
              value={form.stateType}
              onChange={(v) => setForm({ ...form, stateType: (v as WorkflowStateType) })}
              isClearable={false}
            />
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">TAT in Days (optional)</label>
              <input className="form-input" type="number" min={0}
                value={form.slaDays ?? ''}
                onChange={(e) => setForm({ ...form, slaDays: e.target.value === '' ? null : Number(e.target.value) })} />
            </div>
            <div className="form-group">
              <label className="form-label">Escalation Role (optional)</label>
              <input className="form-input" type="text"
                placeholder="e.g. recsuit_ho_manager"
                value={form.escalationRoleCode ?? ''}
                onChange={(e) => setForm({ ...form, escalationRoleCode: e.target.value || null })} />
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </>
  );
};

const AddTransitionModal: React.FC<{
  definitionId: string;
  states: WorkflowState[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ definitionId, states, onClose, onSaved }) => {
  const stateOptions = states.map((s) => ({ value: s.id, label: `${s.stateCode} — ${s.displayName}` }));
  const [form, setForm] = useState<CreateTransitionRequest>({
    fromStateId: '',
    toStateId: '',
    triggerCode: '',
    displayName: '',
    requiresApproval: false,
    displayOrder: 0,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.fromStateId || !form.toStateId || !form.triggerCode || !form.displayName) {
      notificationService.toast.warning('From, To, Trigger, and Display name are required.');
      return;
    }
    if (form.requiresApproval && !form.approverRoleCode) {
      notificationService.toast.warning('Approver role is required when approval is enabled.');
      return;
    }
    setBusy(true);
    try {
      await workflowService.addTransition(definitionId, form);
      notificationService.toast.success('Transition added.');
      onSaved();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Add transition</h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect label="From state *" options={stateOptions} value={form.fromStateId}
                onChange={(v) => setForm({ ...form, fromStateId: v as string })} />
            </div>
            <div className="form-group">
              <SyutSelect label="To state *" options={stateOptions} value={form.toStateId}
                onChange={(v) => setForm({ ...form, toStateId: v as string })} />
            </div>
            <div className="form-group">
              <label className="form-label">Trigger code (lowercase) *</label>
              <input className="form-input" value={form.triggerCode}
                onChange={(e) => setForm({ ...form, triggerCode: e.target.value.toLowerCase() })} />
            </div>
            <div className="form-group">
              <label className="form-label">Display name *</label>
              <input className="form-input" value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Required permission (optional)</label>
            <input className="form-input" value={form.requiredPermission ?? ''}
              onChange={(e) => setForm({ ...form, requiredPermission: e.target.value || undefined })}
              placeholder="e.g. case-management.approve" />
          </div>
          <div className="form-group">
            <label className="form-label">Guard expression (optional)</label>
            <input className="form-input" value={form.guardExpression ?? ''}
              onChange={(e) => setForm({ ...form, guardExpression: e.target.value || undefined })}
              placeholder="e.g. context.amount > 100000 AND context.product_code = 'PL'" />
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.requiresApproval}
                onChange={(e) => setForm({ ...form, requiresApproval: e.target.checked })} />
              Requires approval
            </label>
          </div>
          {form.requiresApproval && (
            <div className="form-group">
              <label className="form-label">Approver role code *</label>
              <input className="form-input" value={form.approverRoleCode ?? ''}
                onChange={(e) => setForm({ ...form, approverRoleCode: e.target.value || undefined })}
                placeholder="e.g. recsuit_admin" />
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </>
  );
};

const AddSpawnRuleModal: React.FC<{
  definitionId: string;
  transitions: WorkflowTransition[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ definitionId, transitions, onClose, onSaved }) => {
  const transitionOptions = transitions.map((t) => ({ value: t.id, label: `${t.triggerCode} — ${t.displayName}` }));
  const [form, setForm] = useState<CreateSpawnRuleRequest>({
    parentTransitionId: '',
    childDefinitionCode: '',
    contextMap: '{}',
    linkType: 'Spawned',
    displayOrder: 0,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.parentTransitionId || !form.childDefinitionCode) {
      notificationService.toast.warning('Parent transition and child definition code are required.');
      return;
    }
    try { JSON.parse(form.contextMap); }
    catch { notificationService.toast.error('Context map is not valid JSON.'); return; }

    setBusy(true);
    try {
      await workflowService.addSpawnRule(definitionId, form);
      notificationService.toast.success('Spawn rule added.');
      onSaved();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Add spawn rule</h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div className="form-group">
            <SyutSelect label="On parent transition *" options={transitionOptions} value={form.parentTransitionId}
              onChange={(v) => setForm({ ...form, parentTransitionId: v as string })} />
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Child definition code *</label>
              <input className="form-input" value={form.childDefinitionCode}
                onChange={(e) => setForm({ ...form, childDefinitionCode: e.target.value })}
                placeholder="e.g. invoice" />
            </div>
            <div className="form-group">
              <SyutSelect label="Link type" options={LINK_TYPES} value={form.linkType}
                onChange={(v) => setForm({ ...form, linkType: v as WorkflowChainLinkType })}
                isClearable={false} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Context map (JSON, JSONPath values prefixed with $.)</label>
            <textarea className="form-textarea" rows={5} value={form.contextMap}
              onChange={(e) => setForm({ ...form, contextMap: e.target.value })}
              placeholder={'{\n  "customer_id": "$.customer_id",\n  "amount": "$.total"\n}'}
              style={{ fontFamily: 'monospace' }} />
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </>
  );
};

export default WorkflowDesigner;
